"use client";
import { LoaderComponent, PaperComponent } from "@/components";
import ValueCarousel from "@/components/ValueCarousel";
import { useRouter } from "@/i18n/routing";
import {
  CurrencyConverter,
  IMAGES,
  PRODUCT_TYPES,
  WEB_COLORS,
} from "@/lib/constants";
import { useGetCustomerQuery } from "@/lib/redux";
import { formatNumber } from "@/lib/utils/common.utils";
import { InfoRounded } from "@mui/icons-material";
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Image from "next/image";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const RecommendedOffer = ({ recommendedOffer, handleEditOffer, extraFees }) => {
  const router = useRouter();

  const productType = useSelector(
    (state) => state?.applicationSlice?.productType,
  );

  const theme = useTheme();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data: customerData } = useGetCustomerQuery();
  const { emi, interestRate, tenure, amount, id, annualInterestRate } =
    recommendedOffer;
  const EmiInfo = useMemo(
    () => [
      {
        label: "EMI Amount",
        val: CurrencyConverter(emi ?? 0),
      },
      { label: "Tenure", val: `${tenure} Months` },
      {
        component: (
          <ValueCarousel
            label="Interest Rate"
            items={[
              {
                value: `${formatNumber(annualInterestRate)}%`,
                suffix: " / Year",
                hidden: !annualInterestRate,
              },
              { value: `${formatNumber(interestRate)}%`, suffix: " / Month" },
            ]}
          />
        ),
      },

      {
        label: "Processing Fee",
        val: `${extraFees?.processingFee}%`,
        subVal: "+ 18% GST",
      },
      {
        label: "Tech/Documentation Fee",
        val: `${extraFees?.techDocumentationFee}%`,
        subVal: "+ 18% GST",
      },
    ],
    [emi, interestRate, annualInterestRate, tenure, extraFees],
  );

  const RenderEmiInfo = ({ info, index }) => {
    const Component = info?.component;
    if (React.isValidElement(Component))
      return (
        <Grid
          key={index}
          item
          bgcolor={"#fff"}
          xs={5.8}
          md={5.8}
          sx={{
            height: { xs: "55px", md: "95px" },
          }}
          alignContent={"center"}
          py={{ xs: "2px", md: "8px" }}
          borderRadius={{ xs: "4px", md: "12px" }}
        >
          {Component}
        </Grid>
      );
    return (
      <Grid
        key={index}
        item
        bgcolor={"#fff"}
        xs={5.8}
        sx={{
          height: { xs: "55px", md: "95px" },
        }}
        md={5.8}
        alignContent={"center"}
        py={{ xs: "2px", md: "8px" }}
        borderRadius={{ xs: "4px", md: "12px" }}
      >
        <Stack>
          <Typography
            color={"#4D6767"}
            fontWeight={400}
            fontSize={{ xs: "8px", md: "16px" }}
          >
            {info.label}
          </Typography>

          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            color={"#004135"}
          >
            <Typography
              color={"#004135"}
              fontWeight={700}
              fontSize={{ xs: "12px", md: "20px" }}
            >
              {info.val}
            </Typography>
            {info.subVal ? (
              <Typography
                fontWeight={400}
                fontSize={{ xs: "6px", sm: "8px", md: "10px" }}
                paddingX={"2px"}
              >
                {info.subVal}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Grid>
    );
  };
  return (
    <div>
      <LoaderComponent open={isLoading} />
      <PaperComponent
        noDrop
        paperStyle={{
          backgroundColor: "#E5F1F1",
          borderRadius: "12px",
          px: { xs: "0", md: "26px" },
          py: { xs: "2px", md: "16px" },
          my: { xs: 1 },
          minWidth: "290px",
        }}
      >
        <Grid
          container
          spacing={1.2}
          justifyContent={"space-between"}
          py={{ xs: "8px", md: "8px" }}
          px={{ xs: "2px", md: "20px" }}
        >
          <Grid item xs={12} md={6}>
            <Stack
              flexDirection={"column"}
              alignItems={{ xs: "center", md: "flex-start" }}
              justifyContent={{ xs: "center", md: "flex-start" }}
              // spacing={3}
              // p={2}
            >
              <Typography
                color={WEB_COLORS.darkTeal}
                fontWeight={500}
                fontSize={{ xs: "14px", md: "20px" }}
                textTransform={"capitalize"}
                textAlign={{ xs: "center", md: "left" }}
              >
                🎉 Congratulations{" "}
                {`${customerData?.data?.firstName?.toLowerCase()} ${customerData?.data?.lastName?.toLowerCase()}`}
                !
              </Typography>
              <Typography
                color={WEB_COLORS.darkTeal}
                fontWeight={400}
                fontSize={{ xs: "14px", md: "16px" }}
                mt={{ xs: 0.5, sm: 1, md: 2 }}
              >
                You’re approved for loan of
              </Typography>
              <Typography
                color={WEB_COLORS.darkTeal}
                fontWeight={700}
                fontSize={{ xs: "24px", md: "52px" }}
              >
                {CurrencyConverter(amount ?? 0)}
              </Typography>
            </Stack>
            {!isMobile && (
              <Box
                textAlign={"center"}
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
                gap={1}
                onClick={() => {
                  // dispatch(setOfferSelectedData({}));
                  setIsLoading(true);
                  router.push(`/${productType}/offer-update?offerId=${id}`);
                }}
              >
                <Typography
                  fontWeight={500}
                  fontSize={{ xs: "12px", md: "18px" }}
                  color={"#004135"}
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Edit offer
                </Typography>
                <Image
                  src={IMAGES.editIcon}
                  alt="edit-icon"
                  width={isMobile ? 12 : 16}
                  style={{ cursor: "pointer" }}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid
              container
              justifyContent={"center"}
              textAlign={"center"}
              alignItems={"stretch"}
              gap={0.4}
            >
              {EmiInfo?.map((info, index) => (
                <RenderEmiInfo info={info} key={index} />
              ))}
            </Grid>
          </Grid>
        </Grid>
        {isMobile && (
          <Box
            textAlign={"center"}
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
            mt={1}
            onClick={() => {
              // dispatch(setOfferSelectedData({}));
              setIsLoading(true);
              router.push(`/${productType}/offer-update?offerId=${id}`);
            }}
          >
            <Typography
              fontWeight={500}
              fontSize={{ xs: "12px", md: "18px" }}
              color={"#004135"}
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Edit offer
            </Typography>
            <Image
              src={IMAGES.editIcon}
              alt="edit-icon"
              width={isMobile ? 12 : 20}
            />
          </Box>
        )}
        <Stack
          flexDirection={"row"}
          gap={0.5}
          py={{ xs: "10px", md: "16px" }}
          px={{ xs: "10px", md: "20px" }}
        >
          <InfoRounded
            color="#004135"
            sx={{
              color: "#004135",
              fontSize: { xs: "16.67px", md: "20px" },
            }}
          />
          <Typography
            align="justify"
            fontWeight={400}
            fontSize={{ xs: "10px", md: "14px" }}
            color={"#407168"}
          >
            Insurance premium won’t reduce your loan amount; it will be added to
            it and paid via monthly EMIs.
          </Typography>
        </Stack>
      </PaperComponent>
    </div>
  );
};

export default RecommendedOffer;
